import {Component, ViewChild, ElementRef, ChangeDetectorRef} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {LoaderService} from 'ngx-satoris';

interface Image {
  width: number,
  height: number,
  data: Uint8Array
}

interface CapturedData {
  nfiq: number,
  data: Uint8Array,
  previewData: Image
}

@Component({
  selector: 'app-fingerprint-modal',
  templateUrl: './fingerprint-modal.component.html',
  styleUrl: './fingerprint-modal.component.scss'
})
export class FingerprintModalComponent {
  @ViewChild('previewCanvas') previewCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('presentFingersTable') missingFingersTableRef!: ElementRef<HTMLFormElement>;
  private ctx: CanvasRenderingContext2D | null = null;
  private previewImageData: ImageData | null = null;
  private fingerprints: {
    [id: number]: {
      nfiq: number,
      data: Uint8Array
    }
  } = {};
  private previewQueue: Image[] = [];
  private isRenderScheduled = false;
  private inCapture = false;
  readonly left = {
    thumb: 0,
    index: 1,
    middle: 2,
    ring: 3,
    little: 4
  };
  readonly right = {
    thumb: 5,
    index: 6,
    middle: 7,
    ring: 8,
    little: 9
  };
  presentFingers: boolean[] = [true, true, true, true, true, true, true, true, true, true];

  constructor(public dialogRef: MatDialogRef<FingerprintModalComponent>,
    private loader: LoaderService,
    private cdr: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    electron.step('fingerprint', false, []);
  }

  private loading(show: boolean): void {
    this.loader.loading(show);
    electron.ext.fingerprint.loading(show);
  }

  onLeftHandClick(): void {
    this.loading(true);
    this.inCapture = true;
    // RSMissingInfo stores left 4 slaps from little to index
    electron.addon.captureFingerprint(RS_CAPTURE.FLAT_LEFT_FOUR_FINGERS, 
      [this.left.little, this.left.ring, this.left.middle, this.left.index]
        .map((index) => !this.presentFingers[index]), 
      this.captureCallback.bind(this), 
      this.previewCallback.bind(this));
  }

  onRightHandClick(): void {
    this.loading(true);
    this.inCapture = true;
    // RSMissingInfo stores right 4 slaps from index to little
    electron.addon.captureFingerprint(RS_CAPTURE.FLAT_RIGHT_FOUR_FINGERS, 
      [this.right.index, this.right.middle, this.right.ring, this.right.little]
        .map((index) => !this.presentFingers[index]), 
      this.captureCallback.bind(this), 
      this.previewCallback.bind(this));
  }

  onThumbsClick(): void {
    this.loading(true);
    this.inCapture = true;
    // RSMissingInfo stores thumbs left then right
    electron.addon.captureFingerprint(RS_CAPTURE.FLAT_TWO_FINGERS, 
      [this.left.thumb, this.right.thumb]
        .map((index) => !this.presentFingers[index]), 
      this.captureCallback.bind(this),
      this.previewCallback.bind(this));
  }

  onCancelClick(): void {
    electron.step('default', false, []);
    this.dialogRef.close();
  }
  onValidateClick(): void {
    electron.step('default', false, []);
    this.dialogRef.close(this.fingerprints);
  }

  isInCapture(): boolean {
    return this.inCapture;
  }

  checkLeftHand(): boolean {
    const presentLeftHandIndices = [this.left.index, this.left.middle, this.left.ring, this.left.little];
    if(this.presentFingers.filter((_, index) => presentLeftHandIndices.includes(index)).every((present) => !present)) {
      return true;
    }
    const keys = Object.keys(this.fingerprints).map(Number);
    return keys.some(k => k >= 7 && k <= 10);
  }

  checkThumbs(): boolean {
    const presentThumbsIndices = [this.left.thumb, this.right.thumb];
    if(this.presentFingers.filter((_, index) => presentThumbsIndices.includes(index)).every((present) => !present)) {
      return true;
    }
    const keys = Object.keys(this.fingerprints).map(Number);
    return keys.some(k => k === 1 || k === 6);
  }

  checkRightHand(): boolean {
    const presentRightHandIndices = [this.right.index, this.right.middle, this.right.ring, this.right.little];
    if(this.presentFingers.filter((_, index) => presentRightHandIndices.includes(index)).every((present) => !present)) {
      return true;
    }
    const keys = Object.keys(this.fingerprints).map(Number);
    return keys.some(k => k >= 2 && k <= 5);
  }

  canValidate(): boolean {
    return this.checkLeftHand() && this.checkThumbs() && this.checkRightHand();
  }

  private captureCallback(errOrResult: Error | {
    [id: number]: CapturedData
  }) {
    this.inCapture = false;
    if(errOrResult instanceof Error) {
      this.loader.loading(true, {
        type: 'error',
        message: errOrResult.message
      });
      this.cdr.detectChanges();
      return;
    }
    const result = errOrResult as {
      [id: number]: CapturedData
    };
    const filtered = Object.fromEntries((
      Object.entries(result).filter(([_, value]) => value !== null)
    ));
    this.fingerprints = {...this.fingerprints, ...filtered};
    this.cdr.detectChanges();
  }

  private previewCallback(error: Error | null, image?: Image) {
    this.loading(false);
    if(error !== null) {
      this.loader.loading(true, {
        type: 'error',
        message: error.message
      });
      this.cdr.detectChanges();
      return;
    }

    this.previewQueue.push(image);

    if(!this.isRenderScheduled) {
      this.isRenderScheduled = true;
      requestAnimationFrame(this.renderPreview.bind(this));
    }
  }

  private renderPreview() {
    const image = this.previewQueue.pop();
    if(!image) {
      this.isRenderScheduled = false;
      return;
    }

    const canvas = this.previewCanvasRef?.nativeElement;
    if(!canvas) {
      this.isRenderScheduled = false;
      return;
    }

    if(!this.ctx) {
      this.ctx = canvas.getContext('2d');
      if(!this.ctx) {
        this.isRenderScheduled = false;
        return;
      }
    }

    if(canvas.width !== image.width || canvas.height !== image.height) {
      canvas.width = image.width;
      canvas.height = image.height;
    }

    if(!this.previewImageData || this.previewImageData.width !== image.width || this.previewImageData.width !== image.height) {
      this.previewImageData = this.ctx.createImageData(image.width, image.height);
    }

    this.previewImageData.data.set(image.data);
    this.ctx.putImageData(this.previewImageData, 0, 0);
    electron.ext.fingerprint.putImageData(image.width, image.height, image.data);

    this.isRenderScheduled = false;
    if(this.previewQueue.length > 0) {
      requestAnimationFrame(this.renderPreview.bind(this));
    }
  }
}
